<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, ref, reactive } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { LayoutOrientation, TableRowHighlight, TableRowType, ButtonDisplayStyle } from "../../Enums/UiEnums";
    import { ICol, RenderModeType, IRowSelection } from "../../lib/ChoiceTableHelpers";
    import { IToolbar, ToolbarStyle, ToolbarSize, ToolbarBackground } from "../../lib/Toolbar"
    import { QuickPayTabType, ICheckInOutDashboardTabSaveItem, CheckInOutDashboardTab } from '../../lib/Models/ScheduleModel';
    import { ModalStyles, popAlert } from "../../lib/Notification"
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { IQuickPayTabItem } from '../../lib/Models/ScheduleModel';
    import { ButtonDisplaySize } from "../../Enums/UiEnums";
    import Toolbar from "../../components/Toolbar.vue"
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import RadioButtonInput from "../../components/RadioButtonInput.vue"
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import * as ChoiceApi from '../../lib/ChoiceApi';

    const privilegesStore = usePrivilegesStore(initPinia());

    const emit = defineEmits(["changeToSetup"]);

    const props = defineProps<{
        tabId: CheckInOutDashboardTab
    }>();

    const setupColumns = ref<ICol[]>([
        { Name: 'Title', DataField: 'title' },
        { Name: 'Label', DataField: 'label' },
        { Name: 'Enabled', DataField: 'enabled', RenderMode: RenderModeType.Selection },
        { Name: 'Expanded', DataField: 'expanded', RenderMode: RenderModeType.Selection },
    ]);

    enum actionToolbar {
        moveUp, 
        moveDown
    }

    const toolbarConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: actionToolbar.moveUp, label: "Move Up", action: ActionButtonStyle.ChveronUp, enabled: false, performAction: () => moveUp()},
            {identifier: actionToolbar.moveDown, label: "Move Down", action: ActionButtonStyle.ChveronDown, enabled: false, performAction: () => moveDown()},
        ]
    }); 

    const formData = reactive<{
        quickPayTabs: IQuickPayTabItem[],
        toolbarExecute: boolean,
        selectedTab?: IQuickPayTabItem,
        tabType: QuickPayTabType
    }>({
        quickPayTabs: [],
        toolbarExecute: false,
        tabType: QuickPayTabType.Dropdown
    });

    onMounted(async () => {
        const result = await ChoiceApi.schedule.QuickPayTabList(props.tabId);
        formData.quickPayTabs = result.tabList ?? [];
        formData.tabType = result.tabType;
    });

    function onSearchRowClick(rowData: IRowSelection,  _rowType: TableRowType, _evt: MouseEvent) : void {
        formData.selectedTab = <IQuickPayTabItem> rowData;
        setMoveUpDownEnable();
    }

    function setMoveUpDownEnable() {
        if (!formData.selectedTab) {
            toolbarConfig.toolbarList[actionToolbar.moveUp].enabled = false;
            toolbarConfig.toolbarList[actionToolbar.moveDown].enabled = false;
        }
        const index = formData.quickPayTabs.findIndex(element => element.quickPayTabId == formData.selectedTab?.quickPayTabId);
        if (index < 0)
            return;
        if (index == 0) {
            toolbarConfig.toolbarList[actionToolbar.moveUp].enabled = false;
            toolbarConfig.toolbarList[actionToolbar.moveDown].enabled = true;
            return;
        } else if (index >= formData.quickPayTabs.length - 1) {
            toolbarConfig.toolbarList[actionToolbar.moveUp].enabled = true;
            toolbarConfig.toolbarList[actionToolbar.moveDown].enabled = false;
            return;
        }
        toolbarConfig.toolbarList[actionToolbar.moveUp].enabled = true;
        toolbarConfig.toolbarList[actionToolbar.moveDown].enabled = true;
    }

    async function saveSetup() {
        console.log(formData.quickPayTabs);
        const tabList : ICheckInOutDashboardTabSaveItem[] = formData.quickPayTabs.map(item => ({quickPayTabId: item.quickPayTabId, enabled: item.enabled, expanded: item.expanded})); 
        const save = {
            tabId: props.tabId,
            tabList: tabList,
            tabType: formData.tabType
        }
        const result = await ChoiceApi.schedule.DashboardTabSave(save);
        if (!result.passed) {
            popAlert("Failed to save the Tab Settings.", "Save", ModalStyles.danger);
            return;
        }
        emit("changeToSetup");
    }
    async function moveUp() {
        if (!formData.selectedTab) {
            popAlert("Please select a row to move up.", "Move Up", ModalStyles.info);
            return;
        }
        const index = formData.quickPayTabs.findIndex(element => element.quickPayTabId == formData.selectedTab?.quickPayTabId);
        if (index <= 0)
            return;

        const temp = formData.quickPayTabs[index-1];
        formData.quickPayTabs[index-1] = formData.quickPayTabs[index];
        formData.quickPayTabs[index] = temp;
        setMoveUpDownEnable();
    }
    async function moveDown() {
        if (!formData.selectedTab) {
            popAlert("Please select a row to move down.", "Move Down", ModalStyles.info);
            return;
        }
        const index = formData.quickPayTabs.findIndex(element => element.quickPayTabId == formData.selectedTab?.quickPayTabId);
        if (index < 0 || index >= formData.quickPayTabs.length - 1)
            return;

        const temp = formData.quickPayTabs[index+1];
        formData.quickPayTabs[index+1] = formData.quickPayTabs[index];
        formData.quickPayTabs[index] = temp;
        setMoveUpDownEnable();
    }
</script>

<template>
    <div class="row">
        <div class="col">
            <Toolbar 
                :configuration="toolbarConfig" 
                :toolbarSize="ToolbarSize.regular" 
                :toolbarStyle="ToolbarStyle.outline" 
                :toolbarBackground="ToolbarBackground.white"
                :spin="formData.toolbarExecute"/>
        </div>
        <div class="col mt-3 text-end">
            <span class="col label-isalus-small me-2">
                Show As 
            </span>
            <RadioButtonInput :value="QuickPayTabType.Dropdown" v-model="formData.tabType" :layout="LayoutOrientation.Horz">                    
                <div class="label-isalus-small">List View</div>
            </RadioButtonInput>
            <RadioButtonInput :value="QuickPayTabType.Tab" v-model="formData.tabType" :layout="LayoutOrientation.Horz">
                <div class="label-isalus-small">Tab View</div>
            </RadioButtonInput>
        </div>
    </div>
    <div class="row">
        <div class="col">
            <ChoiceTable 
                ref="searchTable"
                :columns="setupColumns" 
                :data="formData.quickPayTabs" 
                :show-filters="false" 
                :allowSorting="false"
                fontSize="smaller"
                :rowHighlight="TableRowHighlight.onSingleClick"
                @rowclick="onSearchRowClick"
                :key-value-data-member="'quickPayTabId'">
            </ChoiceTable>
        </div>
    </div>
    <div class="row">
        <div class="col text-end">
            <ActionButtonV2 
                :action="ActionButtonStyle.Save"
                :text="'Save Settings'"
                :size="ButtonDisplaySize.Small" 
                :spinText="'Saving'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="saveSetup"
            />
        </div>
    </div>
</template>

